import { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { FileDown, ExternalLink, Sparkles } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

export type ExportDestination = "download" | "word" | "google" | "gamma";

type Resolver = (v: ExportDestination | null) => void;
interface OpenEvent {
  filename?: string;
  resolve: Resolver;
}

const EVENT_NAME = "thiia:export-destination:open";

/** Ask the user where to open a .docx export: local download, Word Online or Google Docs. */
export function openExportDestinationDialog(meta?: { filename?: string }): Promise<ExportDestination | null> {
  return new Promise((resolve) => {
    window.dispatchEvent(new CustomEvent<OpenEvent>(EVENT_NAME, {
      detail: { ...(meta || {}), resolve },
    }));
  });
}

export default function ExportDestinationHost() {
  const [state, setState] = useState<OpenEvent | null>(null);
  const [wordConnected, setWordConnected] = useState(false);
  const [googleConnected, setGoogleConnected] = useState(false);
  const [gammaConnected, setGammaConnected] = useState(false);

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<OpenEvent>).detail;
      if (!detail) return;
      setState(detail);
    };
    window.addEventListener(EVENT_NAME, handler as EventListener);
    return () => window.removeEventListener(EVENT_NAME, handler as EventListener);
  }, []);

  useEffect(() => {
    if (!state) return;
    let cancelled = false;
    (async () => {
      const { data: sessionData } = await supabase.auth.getSession();
      const userId = sessionData.session?.user?.id;
      if (!userId) return;
      const [w, g, gm] = await Promise.all([
        supabase.from("microsoft_tokens").select("id").eq("user_id", userId).limit(1),
        supabase.from("google_tokens").select("scopes").eq("user_id", userId).limit(1),
        supabase.from("gamma_credentials" as any).select("user_id").eq("user_id", userId).limit(1),
      ]);
      if (cancelled) return;
      setWordConnected(!!(w.data && w.data.length > 0));
      const scopes = (g.data?.[0]?.scopes as string | null) || "";
      setGoogleConnected((g.data?.length || 0) > 0 && scopes.includes("drive.file"));
      setGammaConnected(!!(gm.data && (gm.data as any[]).length > 0));
    })();
    return () => { cancelled = true; };
  }, [state]);

  const close = (v: ExportDestination | null) => { state?.resolve(v); setState(null); };
  const open = !!state;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) close(null); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Où ouvrir le document&nbsp;?</DialogTitle>
          <DialogDescription>
            Choisissez la destination de l'export
            {state?.filename ? <> — <span className="font-mono text-[13px]">{state.filename}</span></> : null}.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-2 py-1">
          {wordConnected && (
            <button
              onClick={() => close("word")}
              className="flex items-center gap-3 rounded-lg border p-3 text-left hover:bg-accent transition"
            >
              <ExternalLink className="h-4 w-4 text-muted-foreground shrink-0" />
              <div className="flex-1">
                <div className="text-sm font-medium">Word Online</div>
                <div className="text-[13px] text-muted-foreground">Utiliser un modèle .docx depuis votre OneDrive</div>
              </div>
            </button>
          )}
          {googleConnected && (
            <button
              onClick={() => close("google")}
              className="flex items-center gap-3 rounded-lg border p-3 text-left hover:bg-accent transition"
            >
              <ExternalLink className="h-4 w-4 text-muted-foreground shrink-0" />
              <div className="flex-1">
                <div className="text-sm font-medium">Google Docs</div>
                <div className="text-[13px] text-muted-foreground">Utiliser un modèle Google Docs depuis votre Drive</div>
              </div>
            </button>
          )}
          {gammaConnected && (
            <button
              onClick={() => close("gamma")}
              className="flex items-center gap-3 rounded-lg border p-3 text-left hover:bg-accent transition"
            >
              <div className="w-4 h-4 rounded-sm bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center shrink-0">
                <Sparkles className="h-2.5 w-2.5 text-white" />
              </div>
              <div className="flex-1">
                <div className="text-sm font-medium">Gamma.app</div>
                <div className="text-[13px] text-muted-foreground">Générer une présentation ou un document visuel par IA</div>
              </div>
            </button>
          )}
          <button
            onClick={() => close("download")}
            className="flex items-center gap-3 rounded-lg border p-3 text-left hover:bg-accent transition"
          >
            <FileDown className="h-4 w-4 text-muted-foreground shrink-0" />
            <div className="flex-1">
              <div className="text-sm font-medium">Télécharger le fichier</div>
              <div className="text-[13px] text-muted-foreground">Appliquer un modèle local et télécharger le .docx</div>
            </div>
          </button>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => close(null)}>Annuler</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
